'use client';

/**
 * Registry global pentru tipuri de documente. Fiecare tip e înregistrat
 * o singură dată (ex. în `apps/app/src/features/document-preview/setup.ts`)
 * și apoi rezolvat lazy când modalul e deschis pentru prima oară pentru tipul ăla.
 *
 * Beneficii la 100k+ users:
 * - Code splitting: chunk per tip de document, încărcat la prima utilizare
 * - Tree-shaking: tipurile neutilizate într-o pagină nu intră în bundle
 * - Cache: configul rezolvat e ținut în memoria modulului (1× per app lifetime)
 */

import type { DocumentTypeConfig, DocumentTypeResolver } from './registry-types';

// In-memory map: type → resolver (eager config sau lazy import function)
const resolvers = new Map<string, DocumentTypeResolver>();

// Cache pentru config-urile deja rezolvate (după primul lazy load)
const cache = new Map<string, DocumentTypeConfig>();

// Promises in-flight (deduplică cereri concurente pentru același tip)
const pending = new Map<string, Promise<DocumentTypeConfig>>();

/**
 * Înregistrează un tip de document.
 *
 * @example
 * // Eager (config import direct):
 * registerDocumentType('invoice', invoiceConfig);
 *
 * @example
 * // Lazy (config code-split):
 * registerDocumentType('invoice', () => import('./invoice-config'));
 */
export function registerDocumentType<TDoc = unknown>(
  type: string,
  resolver: DocumentTypeResolver<TDoc>,
): void {
  resolvers.set(type, resolver as DocumentTypeResolver);
}

/**
 * Rezolvă (potențial lazy) configul pentru un tip de document.
 * Returnează configul sau aruncă dacă tipul nu e înregistrat.
 */
export async function resolveDocumentType(type: string): Promise<DocumentTypeConfig> {
  // Already cached?
  const cached = cache.get(type);
  if (cached) return cached;

  // In-flight request?
  const inflight = pending.get(type);
  if (inflight) return inflight;

  const resolver = resolvers.get(type);
  if (!resolver) {
    throw new Error(
      `[DocumentPreview] Document type "${type}" is not registered. ` +
      `Call registerDocumentType("${type}", config) first.`,
    );
  }

  // Eager config?
  if (typeof resolver !== 'function') {
    cache.set(type, resolver);
    return resolver;
  }

  // Lazy config — issue import + cache promise
  const promise = Promise.resolve(resolver()).then(
    (mod) => {
      const config = ('default' in mod ? mod.default : mod) as DocumentTypeConfig;
      cache.set(type, config);
      pending.delete(type);
      return config;
    },
    (err) => {
      // Registrul nu ține minte eșecul: următoarea previzualizare cheamă din nou resolverul.
      // (Un fișier Turbopack care a dat 404 rămâne însă picat până la reîncărcarea paginii.)
      pending.delete(type);
      throw err;
    },
  );
  pending.set(type, promise);
  return promise;
}

/**
 * Încarcă tipurile cerute de modalul de previzualizare. `onLoaded` primește fiecare config;
 * `onFailed` e chemat O SINGURĂ DATĂ, la primul tip care nu s-a putut încărca. Cauza obișnuită:
 * pagina rulează un build mai vechi decât cel de pe server (tab lăsat deschis peste un deploy),
 * iar fișierele lui nu mai există (404). Întoarce anularea (cleanup-ul effect-ului React).
 */
export function loadDocumentTypes(
  types: string[],
  onLoaded: (type: string, config: DocumentTypeConfig) => void,
  onFailed: (err: unknown) => void,
): () => void {
  let cancelled = false;
  let failed = false;
  for (const type of new Set(types)) {
    // Amânarea cu un microtask e NECESARĂ: InvoiceViewerPanel, NirViewerPanel etc. își
    // înregistrează tipul în propriul useEffect, care rulează DUPĂ effect-ul modalului (copilul).
    // Rezolvat pe loc, tipul ar părea neînregistrat la prima previzualizare.
    Promise.resolve()
      .then(() => resolveDocumentType(type))
      .then(
        (config) => {
          if (!cancelled) onLoaded(type, config);
        },
        (err) => {
          console.error(`[DocumentPreviewModal] failed to resolve type "${type}":`, err);
          if (cancelled || failed) return;
          failed = true;
          onFailed(err);
        },
      );
  }
  return () => {
    cancelled = true;
  };
}

/** Test/dev: șterge cache-ul + resolveri. NU folosi în prod. */
export function _clearDocumentRegistry(): void {
  resolvers.clear();
  cache.clear();
  pending.clear();
}

/** Listează tipurile înregistrate (pentru debug). */
export function getRegisteredDocumentTypes(): string[] {
  return Array.from(resolvers.keys());
}
